import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'

const RiskAssessments = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">What risk assessments are for</Typography>
      <Typography>
        A risk assessment is a structured, dated record of the risks involved
        in transporting a patient — separate from the quick clinical/risk
        flags on the patient record. Use one when a patient's transport needs
        a considered decision: complex mobility, behavioural risk, oxygen,
        bariatric considerations, or a discharge that needs planning between
        the hospital and the transport team.
      </Typography>
      <Typography>
        Risk assessments live on the patient's record — open the patient from{' '}
        <DocLink url="/pts/patients" text="PTS Patients" /> and use the{' '}
        <strong>Risk Assessments</strong> card, which lists every assessment
        with the latest highlighted.
      </Typography>
      <Typography>
        A home risk-assessment visit can also be booked as its own journey —
        the <strong>Risk Assessment</strong> journey type sends a crew to the
        patient's home with no transport leg. See{' '}
        <DocLink url="/patient-transport/bookings" text="Bookings" docsPage />
        .
      </Typography>
      <Alert severity="info" variant="outlined">
        <strong>Who can do this?</strong> Viewing, creating, and editing risk
        assessments from the patient record requires the{' '}
        <strong>Co-Ordinator</strong> PTS permission and a completed
        two-factor authentication check, the same as other patient-record
        access. Crew are the exception, scoped to their own duty: anyone
        assigned to a duty can read a journey's patient assessments (from
        before clock-in until 24 hours after the shift ends), and a
        clocked-in crew member can complete or update an assessment on scene
        — the Risk Assessment visit flow. Hospital portal users can only ever
        read assessments explicitly shared with them (below).
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">What an assessment records</Typography>
      <ul style={{ marginTop: 0, lineHeight: 2 }}>
        <li>
          <strong>Assessment type, date, and overall risk</strong> — plus
          whether the patient is safe to discharge, and an executive summary.
        </li>
        <li>
          <strong>Per-domain ratings</strong> — each risk domain gets its own
          risk rating with findings and controls, so the mitigations are
          recorded next to the risk they answer.
        </li>
        <li>
          <strong>Transport requirements</strong> — mobility category, crew
          requirements, vehicle requirements, equipment, escort required,
          oxygen required, and bariatric considerations. This is the section
          planners and crews care about most.
        </li>
        <li>
          <strong>Discharge team notes</strong> — recommended pathway,
          whether community support is required, and hospital team notes.
        </li>
        <li>
          <strong>Actions</strong> — a list of follow-up actions, each with
          an owner and due date.
        </li>
      </ul>
      <Typography>
        Use <strong>Download PDF</strong> on any assessment to produce a
        formatted copy for sharing outside AmbuNet — for example with a
        receiving unit.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Sharing with the hospital portal</Typography>
      <Typography>
        Each assessment has a <strong>Visible in hospital portal</strong>{' '}
        toggle. When it's on, hospital portal users whose organisation is
        linked to the patient can open the assessment from the Risk
        Assessments column of their journey lists — useful when the ward
        completed the assessment and needs to see what transport was told.
      </Typography>
      <Alert severity="warning" variant="outlined">
        Assessments are <strong>not</strong> visible in the portal by
        default — only ones explicitly marked hospital-visible, and only to
        the organisation they're linked to. Every portal read is written to
        the patient's access log.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">How this reaches the crew</Typography>
      <Typography>
        The journey card (web crew view and mobile app) leads with the
        patient's risk flags, clinical notes, and mobility category from the
        patient record — keep those in step with the assessment's
        conclusions: the assessment is the reasoning, the patient record's
        flags are the operational summary. Crew can also open the patient's
        full risk assessments from a journey's details on either platform,
        and on a Risk Assessment visit the crew complete the assessment on
        scene. See{' '}
        <DocLink url="/patient-transport/patients" text="Patients" docsPage />{' '}
        and{' '}
        <DocLink url="/patient-transport/crew-view" text="Crew View" docsPage />
        .
      </Typography>
    </Stack>
  </Stack>
)

export default RiskAssessments
